import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { useCallback, useState } from "react";

import { db } from "integrations/firebase/firestore";

export type BusinessUnitWriteState =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "error"; error: string };

export function useBusinessUnitWrites() {
  const [state, setState] = useState<BusinessUnitWriteState>({
    status: "idle",
  });

  const run = useCallback(async (write: () => Promise<unknown>) => {
    setState({ status: "submitting" });
    try {
      await write();
      setState({ status: "idle" });
      return true;
    } catch (error) {
      setState({
        status: "error",
        error:
          error instanceof Error ? error.message : "Business unit write failed",
      });
      return false;
    }
  }, []);

  const create = useCallback(
    (name: string, head: string) =>
      run(() =>
        addDoc(collection(db, "business_units"), {
          name,
          head,
          createdAt: serverTimestamp(),
        }),
      ),
    [run],
  );

  const update = useCallback(
    (id: string, name: string, head: string) =>
      run(() =>
        updateDoc(doc(db, "business_units", id), {
          name,
          head,
        }),
      ),
    [run],
  );

  const remove = useCallback(
    (id: string) => run(() => deleteDoc(doc(db, "business_units", id))),
    [run],
  );

  const reset = useCallback(() => setState({ status: "idle" }), []);

  return { state, create, update, remove, reset };
}
